import { PagingParameters } from "@/api/dtos";
import {
  useDeleteComplexLink,
  useGetComplexRelationFields,
  useGetNotRelatedResource,
} from "@/api/related-fields";
import { FieldConfig } from "@/config/forms/general";
import {
  Collector,
  CollectorInMultipleprint,
  Multipleprint,
} from "@/lib/types";
import { ColumnDef, Row } from "@tanstack/react-table";
import { ChevronLeft, PlusIcon, Trash2Icon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, ImageCell, TextCell } from "../table/CustomCell";
import { Button } from "../ui/button";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { LinksTableProps } from "./ArtworkInAuction";
import CollectorInMultipleprintDrawer from "./drawer/CollectorInMultipleDrawer";

interface ComplexLinkProps {
  id: string;
  collector: Collector;
  fields: FieldConfig[] | undefined;
  type: LinkableType;
}

const CollectorInMultipleprints = ({
  id,
  collector,
  fields,
  type,
}: ComplexLinkProps) => {
  const { t } = useTranslation();
  const { data: relatedData } = useGetComplexRelationFields(
    id,
    "collectorInMultipleprints",
    type,
  );

  const { mutate: deleteCollectorInMultipleprint } = useDeleteComplexLink(
    id,
    "collectorInMultipleprints",
    type,
  );

  const [isAddMode, setIsAddMode] = useState(false);

  const handleDeleteRelation = useCallback(
    (id: string) => {
      deleteCollectorInMultipleprint({
        id,
        relation: "collectorInMultipleprint",
      });
    },
    [deleteCollectorInMultipleprint],
  );

  const [searchContent, setSearchContent] = useState<string>("");

  const columns: ColumnDef<CollectorInMultipleprint, any>[] = useMemo(() => {
    return [
      {
        accessorKey:
          "multipleprint.estampesMultiplesDescriptionNumeroEnregistrement",
        header: t("estampesMultiplesDescriptionNumeroEnregistrement"),
        cell: (props) => {
          return <TextCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "vignette",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<CollectorInMultipleprint> }) => {
          return (
            <ImageCell
              value={""}
              attachments={row.original.attachments ?? []}
              domain={"multipleprint"}
            />
          );
        },
      },
      {
        accessorKey: "multipleprint.estampesMultiplesDescriptionTitre",
        header: t("estampesMultiplesDescriptionTitre"),
        cell: (props) => {
          return <TextCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "multipleprint.estampesMultiplesDescriptionTechniqueFRA",
        header: t("estampesMultiplesDescriptionTechniqueFRA"),
        cell: (props) => {
          return <TextCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "estampesDansCollectionneursDescriptionInfos",
        header: t("estampesDansCollectionneursDescriptionInfos"),
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<CollectorInMultipleprint> }) => {
          const data = row.original as CollectorInMultipleprint;
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <Button
                className="font-semibold flex gap-2 p-1"
                variant="ghost"
                type="button"
                onClick={() => handleDeleteRelation(data.id)}
              >
                <Trash2Icon className="w-5 h-5 cursor-pointer text-gray-500" />
              </Button>
              <CollectorInMultipleprintDrawer
                key={row.original.id}
                fields={fields}
                data={data}
                queryId={id}
                type={type}
              />
              <ShowElements
                type="multipleprint"
                id={row.original.id}
                elements={[
                  {
                    label: t(
                      "estampesMultiplesDescriptionNumeroEnregistrement",
                    ),
                    value:
                      row.original.multipleprint
                        .estampesMultiplesDescriptionNumeroEnregistrement,
                    type: "text",
                  },
                  {
                    label: t("estampesMultiplesDescriptionResume"),
                    value:
                      row.original.multipleprint
                        .estampesMultiplesDescriptionResume,
                    type: "richtext",
                  },
                  {
                    label: t("attachments"),
                    value: row.original.multipleprint.attachments,
                    type: "image",
                  },
                ]}
              />
            </div>
          );
        },
      },
    ];
  }, [fields, handleDeleteRelation, id, t, type]);

  const handleAddMode = () => {
    setIsAddMode(!isAddMode);
  };

  return (
    <div className="w-full">
      {isAddMode && (
        <div
          className="flex items-center gap-1 mb-4 cursor-pointer"
          onClick={() => setIsAddMode(false)}
        >
          <ChevronLeft className="w-3 h-3 text-gray-300" />
          <p className="text-sm text-[#006EAB] hover:text-[#005D8D]">
            {t("linkedEntries")}
          </p>
        </div>
      )}
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-lg font-semibold">{t("linkedMultipleprints")}</h2>
        {!isAddMode && (
          <Button variant="outline" onClick={handleAddMode}>
            <PlusIcon className="w-4 h-4 mr-2" />
            {t("add")}
          </Button>
        )}
        {isAddMode && (
          <div className="flex items-center gap-2">
            <Input
              placeholder={t("search")}
              className="w-80"
              value={searchContent}
              onChange={(e) => setSearchContent(e.target.value)}
            />
          </div>
        )}
      </div>
      <CollectorInMultipleprintLinks
        id={id}
        relatedData={relatedData}
        fields={fields}
        collector={collector}
        isAddMode={isAddMode}
        columnsLink={columns}
        searchContent={searchContent}
      />
    </div>
  );
};

type CollectorInMultipleprintLinksProps =
  LinksTableProps<CollectorInMultipleprint> & {
    collector: Collector | undefined;
  };

//Composant pour Oeuvres
const CollectorInMultipleprintLinks = ({
  id,
  relatedData,
  fields,
  collector,
  isAddMode,
  columnsLink,
  searchContent,
}: CollectorInMultipleprintLinksProps) => {
  const { t } = useTranslation();
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: notRelatedMultiples } = useGetNotRelatedResource<Multipleprint>(
    {
      resultType: "multiple",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
    },
  );

  const columnsMultipleprints: ColumnDef<Multipleprint, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "estampesMultiplesDescriptionNumeroEnregistrement",
        header: t("estampesMultiplesDescriptionNumeroEnregistrement"),
        cell: (props) => {
          return <TextCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "estampesMultiplesDescriptionVignette",
        header: "",
        size: 100,
        cell: ({ row }) => {
          return (
            <ImageCell
              value={""}
              attachments={row.original.attachments ?? []}
              domain={"multipleprint"}
            />
          );
        },
      },
      {
        accessorKey: "estampesMultiplesDescriptionTitre",
        header: t("estampesMultiplesDescriptionTitre"),
        cell: (props) => {
          return <HtmlCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "estampesMultiplesDescriptionTechniqueFRA",
        header: t("estampesMultiplesDescriptionTechniqueFRA"),
        size: 100,
        cell: (props) => {
          return <TextCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }) => {
          const data = row.original as Multipleprint;
          if (!collector) return null;
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <ShowElements
                type="multipleprint"
                id={row.original.id}
                elements={[
                  {
                    label: t(
                      "estampesMultiplesDescriptionNumeroEnregistrement",
                    ),
                    value:
                      row.original
                        .estampesMultiplesDescriptionNumeroEnregistrement,
                    type: "text",
                  },
                  {
                    label: t("estampesMultiplesDescriptionResume"),
                    value: row.original.estampesMultiplesDescriptionResume,
                    type: "richtext",
                  },
                  {
                    label: t("attachments"),
                    value: row.original.attachments,
                    type: "image",
                  },
                ]}
              />
              <CollectorInMultipleprintDrawer
                key={row.original.id}
                fields={fields}
                queryId={collector.id}
                type={"collector"}
                data={{
                  id: "",
                  collector: collector,
                  multipleprint: data,
                  estampesDansCollectionneursDescriptionInfos: "",
                }}
              />
            </div>
          );
        },
      },
    ];
  }, [collector, fields, t]);

  const datatableConf = useMemo(
    () =>
      isAddMode ? (
        <DataTable
          columns={columnsMultipleprints}
          data={notRelatedMultiples?.data ?? []}
          pagination={{
            pagination: getPagination,
            setPage: setPagination,
            totalSize: notRelatedMultiples?.totalSize ?? 0,
          }}
          options={{
            enableColumnResizing: true,
          }}
        />
      ) : (
        <DataTable
          columns={columnsLink}
          data={relatedData ?? []}
          options={{
            enableColumnResizing: true,
          }}
        />
      ),
    [
      columnsLink,
      columnsMultipleprints,
      getPagination,
      isAddMode,
      notRelatedMultiples?.data,
      notRelatedMultiples?.totalSize,
      relatedData,
    ],
  );

  return datatableConf;
};

export default CollectorInMultipleprints;
